import { create } from "zustand";
import { registerOrgScopedReset } from "@/lib/org-scope";

/** A fenced block from a response, opened in the workspace. See `codePreview.ts`. */
export interface CodePayload {
  /** The fence's own tag — the label. */
  language: string;
  /** The Monaco language id. */
  monaco: string;
  content: string;
  /** A filename, only when the block named one. Never invented. */
  title: string | null;
  /** `shell` renders as a transcript: no diff gutter, no filename. */
  kind: "code" | "shell";
  /**
   * Set when this code is the reader's OWN unsent paste, and names the composer attachment it came
   * from — which is what makes the panel editable for it and nothing else.
   *
   * `CodeSection` is read-only by default for a documented reason: a block the ASSISTANT wrote has
   * nowhere to be saved back to, and an editable buffer with no destination is the fake capability
   * §11 forbids. A paste is the opposite case — it has not been sent, the reader owns it, and
   * "saving" means updating the attachment still sitting in their composer. The id IS that
   * destination, so its presence is the whole condition rather than a second `editable` flag that
   * could disagree with it.
   */
  attachmentId?: string;
}

/**
 * Which inspection surface is open, if any — the state behind the right-hand workspace.
 *
 * WHY A STORE AND NOT COMPONENT STATE. It is written from the transcript (a reference card), from
 * the action bar (an action that needs a surface the bar does not hold), and from the workspace
 * itself (close, switch section). Those three live in unrelated parts of the tree, which is §5's
 * rule 5 — "must be read from an unrelated tree" — and the only case in this feature that qualifies.
 * It holds two scalars and no server data; nothing here duplicates `runStateStore`.
 *
 * ONE SECTION AT A TIME, deliberately. A workspace that shows plan AND changes AND deployment at
 * once is the dashboard the conversation was supposed to stop being — it would just move the
 * eleven-panel stack to the right-hand side. `section` is a single value, not a set.
 *
 * CLOSED IS THE DEFAULT AND IT REOPENS EMPTY. There is no "last section" memory: a workspace that
 * springs open on the next run showing the previous run's plan is worse than one the reader opens
 * when they want it, and the contract gives no promise that the previous section even exists on the
 * new run.
 *
 * ONE OWNER-APPROVED EXCEPTION (2026-10-14): when a task's changeset is first proposed in the live
 * conversation, `useLaneV2AutoOpen` opens the `task` section on that task (`openedBy: "auto"`).
 * It is narrow by construction — once per `task_id:package_hash`, never on attach/reload, never on
 * a narrow screen, never over an already-open section, never while the document is hidden — and an
 * auto-open does not move focus (the reader may be typing in the composer). Everything else still
 * opens only on the reader's click.
 */
export type WorkspaceSection =
  | "artifact"
  /**
   * A code or shell block the user opened from a response.
   *
   * The only section whose subject is not in `RunState`: its content came from a `text` part's
   * fenced block, which the reducer already accumulated into `part.body.text`. It travels in
   * `payload` rather than being re-fetched — the bytes are in the browser, and asking for them
   * again would make the same content have two sources.
   */
  | "code"
  /**
   * A lane-v2 TASK (plan → build → package → validation), by `focusRef` = task id.
   *
   * Its subject is not in `RunState` either: it is the session store's R3 snapshot for that task
   * (`laneV2SessionStore.snapshots`), which the section reads by id. Opened from the task cards in
   * the conversation and from a task's own "view item" action (`taskItem`).
   */
  | "task";

interface WeaveWorkspaceStore {
  /** `null` = closed. The conversation is full width. */
  section: WorkspaceSection | null;
  /**
   * The `Part.ref` that opened the section, when the section needs one.
   *
   * Only `artifact` genuinely does — the others have exactly one subject per run (`RunState.plan`,
   * `.changeset`, `.deploy`, `.evidence`), so their surface finds it in the store without an id.
   * Kept for all of them anyway so a card never has to know which sections care.
   */
  focusRef: string | null;
  /**
   * Content for a section that has no subject in `RunState` — today only `code`.
   *
   * Deliberately NOT a cache: it is set by the click that opens the section and cleared when the
   * workspace closes. Nothing reads it as a source of truth about the run, and no other section
   * uses it.
   */
  payload: CodePayload | null;
  /**
   * The work item the `task` section should show the body of (R4), or null for the task overview.
   *
   * Set by a task's "view item" action, cleared by the section's own Back and by every other opener:
   * it is navigation state inside one section, not a cache of the body — the section fetches R4
   * itself when this names an item.
   */
  taskItem: string | null;
  /**
   * Who opened the current section. `auto` = `useLaneV2AutoOpen` (no focus is moved into the panel);
   * `user` = every click-driven opener. Meaningless while `section` is null.
   */
  openedBy: "auto" | "user";
  open: (section: WorkspaceSection, focusRef?: string | null) => void;
  /** Open the `task` section on one task, optionally straight onto one of its work items. */
  openTask: (taskId: string, workItemId?: string | null, openedBy?: "auto" | "user") => void;
  /** Open the `code` section on a block the user clicked in the conversation. */
  openCode: (payload: CodePayload) => void;
  close: () => void;
  /** Open, or close if that same section is already open — what a card's own affordance does. */
  toggle: (section: WorkspaceSection, focusRef?: string | null) => void;
}

export const useWeaveWorkspaceStore = create<WeaveWorkspaceStore>((set, get) => ({
  section: null,
  focusRef: null,
  payload: null,
  taskItem: null,
  openedBy: "user",
  // Any other section clears the payload: leaving it set would keep a response's code alive behind
  // an unrelated panel, and a later `openCode` could then be told it was already showing.
  open: (section, focusRef = null) => set({ section, focusRef, payload: null, taskItem: null, openedBy: "user" }),
  openTask: (taskId, workItemId = null, openedBy = "user") =>
    set({ section: "task", focusRef: taskId, payload: null, taskItem: workItemId, openedBy }),
  openCode: (payload) =>
    set((state) =>
      // Re-clicking the SAME block closes it, like every other opener. Identity is the content plus
      // its title, because a response can legitimately repeat a snippet and two identical blocks
      // are the same thing to a reader.
      state.section === "code" &&
      state.payload?.content === payload.content &&
      (state.payload?.title ?? null) === (payload.title ?? null)
        ? { section: null, focusRef: null, payload: null, taskItem: null, openedBy: "user" }
        : { section: "code", focusRef: null, payload, taskItem: null, openedBy: "user" },
    ),
  close: () => set({ section: null, focusRef: null, payload: null, taskItem: null, openedBy: "user" }),
  toggle: (section, focusRef = null) => {
    const current = get();
    // Re-opening the SAME artifact closes it; a different one switches rather than closing, because
    // "open document B" while A is open is a switch and not a dismissal.
    const same = current.section === section && (current.focusRef ?? null) === (focusRef ?? null);
    set(
      same
        ? { section: null, focusRef: null, payload: null, taskItem: null, openedBy: "user" }
        : { section, focusRef, payload: null, taskItem: null, openedBy: "user" },
    );
  },
}));

/*
 * A workspace open on one tenant's changeset must not survive a switch to another: the section
 * would stay open, `runStateStore` would be reset underneath it, and the reader would be looking at
 * an empty panel labelled with the previous org's subject. Same registration every other
 * multi-agent store makes (§6).
 */
registerOrgScopedReset("weaveWorkspaceStore", () => useWeaveWorkspaceStore.getState().close());

export const selectWorkspaceSection = (s: WeaveWorkspaceStore): WorkspaceSection | null => s.section;
export const selectWorkspaceRef = (s: WeaveWorkspaceStore): string | null => s.focusRef;
export const selectWorkspacePayload = (s: WeaveWorkspaceStore): CodePayload | null => s.payload;
export const selectWorkspaceTaskItem = (s: WeaveWorkspaceStore): string | null => s.taskItem;
export const selectWorkspaceOpenedBy = (s: WeaveWorkspaceStore): "auto" | "user" => s.openedBy;
